import axios from "axios";
import Cookies from "js-cookie";
import {
  RefreshToken,
  setAuthTokens,
  handleAuthenticationFailure,
} from "./AuthenticationData.ts";
import { BASE_URL } from "./Context/config";
import { toast } from "react-toastify";

const api = axios.create({
  baseURL: BASE_URL,
});

api.interceptors.request.use(
  async (config) => {
    const authToken = Cookies.get("authToken");

    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper function to check if we should attempt token refresh based on current context
const shouldAttemptTokenRefresh = (originalRequest) => {
  // Don't refresh tokens for auth-related endpoints
  const authEndpoints = [
    "/Auth/Login",
    "/Auth/Register",
    "/Auth/RefreshToken",
    "/Login",
  ];
  if (
    authEndpoints.some((endpoint) => originalRequest.url?.includes(endpoint))
  ) {
    return false;
  }

  // Check if we're on a public page where 401s are expected
  const currentPath = window.location.pathname;
  const publicPaths = [
    "/Login",
    "/Signup",
    "/ForgotPasswordEmail",
    "/VerifyMail",
    "/VerifyingMail",
    "/VerifyPasswordChanging",
    "/Profile",
    "/profile",
  ];

  // If we're on a public page, don't attempt refresh unless the user has tokens
  // (indicating they were previously authenticated)
  if (publicPaths.some((path) => currentPath.startsWith(path))) {
    const hasTokens = Cookies.get("authToken") && Cookies.get("refreshToken");
    return hasTokens; // Only refresh if user was previously authenticated
  }

  // For protected pages (like /admin/*), always attempt refresh
  // Note: /Profile pages are public and should not trigger automatic token refresh
  if (currentPath.startsWith("/admin")) {
    return true;
  }

  // For other pages, check if user has tokens
  return Cookies.get("authToken") && Cookies.get("refreshToken");
};

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      // Check if we should attempt token refresh based on context
      if (!shouldAttemptTokenRefresh(originalRequest)) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      const refreshToken = Cookies.get("refreshToken");

      if (refreshToken) {
        try {
          const response = await RefreshToken();

          if (response && response.data && response.data.accessToken) {
            setAuthTokens(
              response.data.accessToken,
              response.data.refreshToken,
              response.data.expiresIn
            );
            originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
            return axios(originalRequest);
          } else {
            console.error("Token refresh failed: Invalid response from server");
            handleAuthenticationFailure(null, toast);
            return Promise.reject({
              redirectToLogin: true,
              message: "Session expired. Please log in again.",
            });
          }
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          handleAuthenticationFailure(null, toast);
          return Promise.reject({
            redirectToLogin: true,
            message: "Session expired. Please log in again.",
          });
        }
      } else {
        console.error("No refresh token available for protected resource");
        handleAuthenticationFailure(null, toast);
        return Promise.reject({
          redirectToLogin: true,
          message: "Session expired. Please log in again.",
        });
      }
    }

    return Promise.reject(error);
  }
);

export default api;
